import "./style.scss";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/src/store";
import {
  setColumnWidth,
  toggleRowSelection,
  toggleAllRows,
  setActualFieldValue,
} from "@/src/store/slices/ApplicationPage/applicationsTableSlice";
import { onlineApplicationsStarsConst, onlineApplicationsStatusConst } from "@/src/constants/applicationOnlineConsts";
import { IApplyInfoData, IFiltersList, IRowData } from "@/src/types/applicationOnline";
import ArrowFilter from "@/src/svg/applicationsPage/ArrowFilter";
import ArrowSvg from "@/src/svg/applicationsPage/APPopUp/Documents/ArrowSvg";
import APFilterPopUp from "../APFilterPopUp/APFilterPopUp";
import ApplicationStarIcon from "../ApplicationStarIcon";
import VerticalLine from "../../VerticalLine/VerticalLine";
import { applicationPageRequests } from "@/src/api/applicationPageRequests";
import GuidelineSvg from "@/src/svg/sidebar/GuidelineSvg";
import { studentsRequests } from "@/src/api/studentsRequests";
import { fetchApplicantUserInfo } from "@/src/utils/applicants/fetchApplicantUserInfo";
import { usePushNotifications } from "@/src/components/CustomUtils/PushNotifications/PushNotificationsProvider";
import {
  buildApplicationsFiltersList,
  filterApplicationsRows,
  mergeApplicationsFiltersList,
} from "@/src/utils/applications/filterApplicationsRows";

// vocabulary id → accessor for data
const columnAccessors: Record<string, (row: IApplyInfoData) => React.ReactNode> = {
  fullName: (row) => row.fullname,
  email: (row) => row.email,
  phone: (row) => row.phone,
  country: (row) => row.country,
  nationality: (row) => row.nationality,
  dateOfBirth: (row) => row.dateOfBirth,
  address: (row) => row.address,
  passportNo: (row) => row.passportNo,
  passportIssued: (row) => row.passportIssued,
  validPassport: (row) => row.validPassport,

  date: (row) => row.date,
  id: (row) => row.id,
  applicant_id: (row) => (row.applicant_id != null ? String(row.applicant_id) : ""),
  status: (row) => row.form_status,
  type: (row) => row.form_type,
  course: (row) => row.course,
  levelEducation: (row) => row.levelEducation ?? "",
  updated: (row) => row.updated,
  assignee: (row) => row.assignee ?? "",
  comments: (row) => row.text ?? "",
  is_read: (row) => (row.is_read ? "✓" : "—"),
  color_star: (row) => row.color_star,
};

interface APBodyProps {
  headerCheckboxRef: React.RefObject<HTMLInputElement | null>;
  setIsApPopUpFullName: React.Dispatch<React.SetStateAction<boolean>>;
  setCurrentDataPopUp: React.Dispatch<React.SetStateAction<IRowData["userInfo"] | undefined>>;
  setCurrentStudentId: React.Dispatch<React.SetStateAction<number | undefined>>;
  setCurrentApplyInfoId: React.Dispatch<React.SetStateAction<number | undefined>>;
  tableFiltersList: IFiltersList | undefined;
  isShowSettingsModal: boolean;
}

const APBody: FC<APBodyProps> = ({
  headerCheckboxRef,
  setIsApPopUpFullName,
  setCurrentDataPopUp,
  setCurrentStudentId,
  setCurrentApplyInfoId,
  tableFiltersList,
  isShowSettingsModal,
}) => {
  const dispatch = useDispatch();
  const { notify } = usePushNotifications();
  const { visibleColumns, data, selectedRows } = useSelector((state: RootState) => state.applicationsTable);
  const filterResults = useSelector((state: RootState) => state.applicationsFilters.filterResults);
  const safeData = Array.isArray(data) ? data : [];
  const filteredData = useMemo(() => filterApplicationsRows(safeData, filterResults), [safeData, filterResults]);
  const mergedFiltersList = useMemo(
    () => mergeApplicationsFiltersList(tableFiltersList, buildApplicationsFiltersList(safeData)),
    [tableFiltersList, safeData],
  );
  const [filterPopUp, setFilterPopUp] = useState<string | null>(null);
  const [openTableParamMenu, setOpenTableParamMenu] = useState<string>("");
  const [openTableParamMenuRowId, setOpenTableParamMenuRowId] = useState<number | null>(null);
  const allChecked = selectedRows.length === filteredData.length && filteredData.length > 0;
  const someChecked = selectedRows.length > 0 && selectedRows.length < filteredData.length;

  const [fixedColumnIndex, setFixedColumnIndex] = useState<number | null>(null);
  const [isAttach, setIsAttach] = useState<number | null>(null);

  const handleAttach = (index: number) => {
    if (isShowSettingsModal) {
      setFixedColumnIndex((prev) => (prev === index ? null : index));
      setIsAttach(index);
      if (isAttach === index) {
        setIsAttach(null);
      }
    }
  };

  const CHECKER_WIDTH = 4.12;
  const getStickyStyles = (index: number, section: "thead" | "tbody") => {
    if (fixedColumnIndex === null || index > fixedColumnIndex) return {};
    const left = CHECKER_WIDTH + visibleColumns.slice(0, index).reduce((acc, col) => acc + (col.width || 6), 0);
    return {
      position: "sticky" as const,
      left: `${left}rem`,
      zIndex: section === "thead" ? 7 : 5,
    };
  };
  // ====== drag-to-resize ======
  const dragData = useRef<{ startX: number; thId: string | null; startWidth: number }>({
    startX: 0,
    thId: null,
    startWidth: 0,
  });

  const onMouseDown = (e: React.MouseEvent<HTMLDivElement>, thId: string, startWidth: number) => {
    if (!isShowSettingsModal) return;
    e.stopPropagation();
    e.preventDefault();
    dragData.current = { startX: e.clientX, thId, startWidth };
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  };

  const onMouseMove = (e: MouseEvent) => {
    if (!isShowSettingsModal || !dragData.current.thId) return;
    const deltaRem = (e.clientX - dragData.current.startX) / 16;
    let newWidth = dragData.current.startWidth + deltaRem;
    if (newWidth < 2) newWidth = 2;
    dispatch(setColumnWidth({ id: dragData.current.thId, width: newWidth }));
  };

  const onMouseUp = () => {
    if (!isShowSettingsModal) return;
    window.removeEventListener("mousemove", onMouseMove);
    window.removeEventListener("mouseup", onMouseUp);
  };
  // ====== drag-to-scroll (horizontal, thead only) ======
  const scrollRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const scrollStartX = useRef(0);
  const scrollStartScrollLeft = useRef(0);

  const isTheadScrollTarget = (target: EventTarget | null) => {
    if (!(target instanceof HTMLElement)) return false;
    return (
      !target.closest("input") &&
      !target.closest(".custom-checkbox") &&
      !target.closest(".cell-wrapper-arrow") &&
      !target.closest(".cell-wrapper-attach") &&
      !target.closest(".vertical-line") &&
      !target.closest(".applications-page-filter")
    );
  };

  const onTheadScrollMouseDown = (e: React.MouseEvent) => {
    if (!scrollRef.current || !isTheadScrollTarget(e.target)) return;
    isDragging.current = true;
    scrollStartX.current = e.clientX;
    scrollStartScrollLeft.current = scrollRef.current.scrollLeft;
  };

  const onTheadScrollMouseMove = (e: React.MouseEvent) => {
    if (!isDragging.current || !scrollRef.current) return;
    e.preventDefault();
    const dx = e.clientX - scrollStartX.current;
    scrollRef.current.scrollLeft = scrollStartScrollLeft.current - dx;
  };

  const stopTheadScrollDrag = () => {
    isDragging.current = false;
  };

  const onTheadScrollTouchStart = (e: React.TouchEvent) => {
    if (!scrollRef.current || !isTheadScrollTarget(e.target)) return;
    isDragging.current = true;
    scrollStartX.current = e.touches[0].clientX;
    scrollStartScrollLeft.current = scrollRef.current.scrollLeft;
  };

  const onTheadScrollTouchMove = (e: React.TouchEvent) => {
    if (!isDragging.current || !scrollRef.current) return;
    const dx = e.touches[0].clientX - scrollStartX.current;
    scrollRef.current.scrollLeft = scrollStartScrollLeft.current - dx;
  };

  const handleTableFullNameEvent = async (value: IApplyInfoData) => {
    setCurrentStudentId(value.applicant_id);
    setCurrentApplyInfoId(value.id);
    try {
      const userInfo = await fetchApplicantUserInfo(value.applicant_id);
      if (userInfo) setCurrentDataPopUp(userInfo);
    } catch (error) {
      console.log("applicant request error:", error);
    }

    setIsApPopUpFullName(true);
  };

  const handleEventChangeFieldValue = async (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    id: number,
    value: string,
    actualPopUp: string,
  ) => {
    event.stopPropagation();

    setOpenTableParamMenuRowId(null);
    setOpenTableParamMenu("");

    let actualFieldForChange: string | undefined;

    switch (actualPopUp) {
      case "star":
        actualFieldForChange = "color_star";
        break;
      case "status":
        actualFieldForChange = "form_status";
        break;
      default:
        console.warn(`Неизвестный тип actualPopUp: ${actualPopUp}`);
        return;
    }
    console.log("actualFieldForChange", actualFieldForChange);

    if (!actualFieldForChange) return;
    const targetRow = safeData.find((item) => item.id === id);
    const isStatusChange = actualPopUp === "status";
    const previousStatus = targetRow?.form_status;
    const nextStatus = value;
    const nextStatusLabel = onlineApplicationsStatusConst.find((item) => item.id === nextStatus)?.label ?? nextStatus;

    if (isStatusChange && previousStatus === nextStatus) return;

    dispatch(setActualFieldValue({ id, fieldActualValue: value, keyField: actualFieldForChange }));
    try {
      await applicationPageRequests.update({
        id,
        [actualFieldForChange]: value,
      });

      if (isStatusChange && targetRow?.applicant_id != null) {
        await studentsRequests.createHistory({
          student_id: targetRow.applicant_id,
          text: `Changes have been made to status updated to "${nextStatusLabel}"`,
        });
      }
      notify({
        title: isStatusChange ? "Status updated" : "Star updated",
        message: isStatusChange ? `Application status changed to "${nextStatusLabel}".` : "Application marker updated.",
        type: "success",
      });
    } catch (error) {
      console.error("Ошибка при обновлении:", error);
      notify({
        title: "Update failed",
        message: "Could not update application field.",
        type: "error",
      });
    }
  };

  const toggleTableParamMenu = (menuType: string, rowId: number) => {
    if (openTableParamMenu === menuType && openTableParamMenuRowId === rowId) {
      setOpenTableParamMenu("");
      setOpenTableParamMenuRowId(null);
    } else {
      setOpenTableParamMenu(menuType);
      setOpenTableParamMenuRowId(rowId);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest(".status-menu") && !target.closest(".star-menu")) {
        setOpenTableParamMenu("");
        setOpenTableParamMenuRowId(null);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // A function for replacing a cell value if needed when rendering information from the backend.
  const replacingBackendValueToCustom: Record<string, string> = {
    APPLY_ONLINE: "Apply Online",
    NEW: "New",
    // PENDING: "Pending",
  };

  return (
    <div
      className="applications-page-body"
      ref={scrollRef}
      onMouseMove={onTheadScrollMouseMove}
      onMouseUp={stopTheadScrollDrag}
      onMouseLeave={stopTheadScrollDrag}
      onTouchMove={onTheadScrollTouchMove}
      onTouchEnd={stopTheadScrollDrag}
      onTouchCancel={stopTheadScrollDrag}
    >
      <table>
        <thead onMouseDown={onTheadScrollMouseDown} onTouchStart={onTheadScrollTouchStart}>
          <tr>
            <th id="table-checker" style={{ width: `${CHECKER_WIDTH}rem` }}>
              <div className="cell-wrapper">
                <label className={`custom-checkbox ${someChecked && !allChecked ? "indeterminate" : ""}`}>
                  <input type="checkbox" checked={allChecked} ref={headerCheckboxRef} onChange={() => dispatch(toggleAllRows())} />
                  <span className="checkmark"></span>
                </label>
                <div
                  className={`cell-wrapper-arrow ${filterPopUp === "color_star" ? "rotate" : ""}`}
                  onClick={() => setFilterPopUp(filterPopUp === "color_star" ? null : "color_star")}
                >
                  <ArrowFilter />
                </div>
                <div className="vertical-line">
                  <VerticalLine />
                </div>
                <APFilterPopUp
                  id="color_star"
                  tableFiltersList={mergedFiltersList}
                  filterPopUp={filterPopUp}
                  setFilterPopUp={setFilterPopUp}
                />
              </div>
            </th>
            {visibleColumns.map(({ title, id, width }, index) => (
              <th
                key={id}
                style={{
                  width: `${width}rem`,
                  minWidth: `${width}rem`,
                  ...getStickyStyles(index, "thead"),
                }}
              >
                <div className="cell-wrapper">
                  <p>{title}</p>
                  <div className="cell-wrapper-actions">
                    {(isShowSettingsModal || isAttach === index) && (
                      <div className="cell-wrapper-attach" onClick={() => handleAttach(index)}>
                        <GuidelineSvg />
                      </div>
                    )}
                    {(id === "date" ||
                      id === "updated" ||
                      id === "status" ||
                      id === "type" ||
                      id === "country" ||
                      id === "course" ||
                      id === "assignee") && (
                      <div
                        className={`cell-wrapper-arrow ${filterPopUp === id ? "rotate" : ""}`}
                        onClick={() => setFilterPopUp(filterPopUp === id ? null : id)}
                      >
                        <ArrowFilter />
                      </div>
                    )}
                  </div>
                  <div className="vertical-line" onMouseDown={(e) => onMouseDown(e, id, width || 6)} style={{ cursor: "ew-resize" }}>
                    <VerticalLine />
                  </div>
                  <APFilterPopUp id={id} tableFiltersList={mergedFiltersList} filterPopUp={filterPopUp} setFilterPopUp={setFilterPopUp} />
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filteredData.length === 0 && (
            <tr>
              <td colSpan={visibleColumns.length + 1}>
                <div className="cell-wrapper">There is nothing here right now.</div>
              </td>
            </tr>
          )}
          {filteredData.map((row) => {
            // console.log("row", row);
            return (
              <tr
                key={row.id}
                className={[row.is_read ? "" : "form-not-read", selectedRows.includes(row.id) ? "table-row-selected" : ""]
                  .filter(Boolean)
                  .join(" ")}
              >
                <td id="table-checker" style={{ width: `${CHECKER_WIDTH}rem` }}>
                  <div className="cell-wrapper">
                    <label className="custom-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedRows.includes(row.id)}
                        onChange={() => dispatch(toggleRowSelection(row.id))}
                      />
                      <span className="checkmark"></span>
                    </label>
                    <div
                      className={`star-icon ${row.color_star}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleTableParamMenu("star", row.id);
                      }}
                    >
                      <ApplicationStarIcon color={row.color_star} />
                    </div>
                    {openTableParamMenu === "star" && openTableParamMenuRowId === row.id && (
                      <div className="star-menu">
                        {onlineApplicationsStarsConst.map(({ color, description }) => (
                          <div
                            key={color}
                            className={`star-option ${color}`}
                            onClick={(e) => handleEventChangeFieldValue(e, row.id, color, "star")}
                          >
                            <ApplicationStarIcon color={color} />
                            {description && description !== "" && <div className="tooltip">{description}</div>}
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="vertical-line">
                      <VerticalLine />
                    </div>
                  </div>
                </td>
                {visibleColumns.map(({ id, width }, index) => {
                  const rawValue = String(columnAccessors[id]?.(row) ?? "");
                  const displayValue = replacingBackendValueToCustom[rawValue] ?? rawValue;
                  return (
                    <td
                      key={id}
                      style={{
                        width: `${width}rem`,
                        minWidth: `${width}rem`,
                        ...getStickyStyles(index, "tbody"),
                      }}
                    >
                      <div
                        className={`cell-wrapper ${id === "fullName" || id === "status" ? "clicable" : ""}`}
                        {...(id === "fullName" && { onClick: () => handleTableFullNameEvent(row) })}
                        {...(id === "status" && {
                          onClick: (e) => {
                            e.stopPropagation();
                            toggleTableParamMenu("status", row.id);
                          },
                        })}
                      >
                        {id === "status"
                          ? onlineApplicationsStatusConst
                              .filter((item) => row.form_status === item.id)
                              .map((item) => (
                                <div key={item.id} className={`status-option ${item.id}`}>
                                  <span className="status-label">{item.label}</span>
                                </div>
                              ))
                          : null}
                        {id !== "status" ? (
                          id === "fullName" ? (
                            <div className="full-name-cell">
                              <p>{displayValue}</p>
                              <ArrowSvg />
                            </div>
                          ) : (
                            <div>{displayValue}</div>
                          )
                        ) : null}
                        {id === "status"
                          ? openTableParamMenu === "status" &&
                            openTableParamMenuRowId === row.id && (
                              <div className="status-menu">
                                {onlineApplicationsStatusConst.map(({ label, id }) => (
                                  <div
                                    key={id}
                                    className={`status-option ${id}`}
                                    onClick={(e) => handleEventChangeFieldValue(e, row.id, id, "status")}
                                  >
                                    <span className="status-label">{label}</span>
                                  </div>
                                ))}
                              </div>
                            )
                          : null}
                        <div className="vertical-line">
                          <VerticalLine />
                        </div>
                      </div>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default APBody;
